import React from 'react';
import { View } from 'react-native';

import { SvgIcon } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

/** Pixel metrics of the teardrop pin. Markers render in screen-space (fixed size, they
 *  don't scale with map zoom), so these are literal pixels — bumped up from the Figma
 *  26px which reads too small on-device. Shared so a marker can size its thumbnail to
 *  the head with explicit pixels: on Android, percentage sizes inside a <Marker> custom
 *  view collapse to 0, which is why an <Image> with width/height '100%' renders blank. */
export const pinMetrics = (scale: (size: number) => number) => {
  const width = scale(40);
  const headSize = width * 0.76;
  return {
    width,
    height: (width * 35) / 26,
    headSize,
    headTop: width / 2 - headSize / 1.94,
  };
};

/** The teardrop pin shell (ic-pin.svg) with a circular slot in its head for content
 *  (a thumbnail or a cluster count). */
export function PinFrame({
  children,
  isCluster,
}: {
  children: React.ReactNode;
  isCluster?: boolean;
}) {
  const { scale } = useScaling();
  const styles = useStyles();

  const { width, height, headSize, headTop } = pinMetrics(scale);

  return (
    <View style={[styles.pin, { width, height }]}>
      <SvgIcon name="IcPin" width={width} height={height} />
      <View
        style={[
          styles.pinHead,
          isCluster
            ? {
                backgroundColor: guardianColors['Main/Main'],
                borderColor: guardianColors['Main/Main'],
              }
            : { backgroundColor: colors[0] },
          { width: headSize, height: headSize, borderRadius: headSize / 2, top: headTop },
        ]}>
        {children}
      </View>
    </View>
  );
}

const useStyles = createStyles(({ scale }) => ({
  pin: {
    alignItems: 'center',
  },
  pinHead: {
    // No `overflow: 'hidden'` here: on the New Architecture (Fabric) + Android, a View
    // with overflow:hidden inside a <Marker> stops its <Image> child from rendering into
    // the marker's snapshot (blank pin). The thumbnail clips itself via borderRadius instead.
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: scale(1),
    borderColor: colors['0'],
    backgroundColor: colors['0'],
  },
}));
